import { FC, useCallback, useRef, useState } from 'react'
import { Box, Stack } from '@mui/material'
import Webcam from 'react-webcam'
import { TestStripOverlay } from '../../../Upload/components/TestStripOverlay'
import { ContainedButton, OutlinedButton } from '../../../../../components/Button'
import { dataURLtoFile } from '../../../../../utils/uploadHelper'
import { useSnackbar } from '../../../../../hooks/snackbar/useSnackbar'

interface CameraOptionsProps {
  setCurrentState: Function,
  handleImageDrop: Function,
}

export const CameraOptions :FC<CameraOptionsProps> = ({setCurrentState, handleImageDrop}) => {
  const { showError } = useSnackbar()
  const webcamRef = useRef<Webcam>(null)
  const [isStripPositioned, setIsStripPositioned] = useState(false)
  const [guidanceMessage, setGuidanceMessage] = useState<string>('')
  const [stream, setStream] = useState<MediaStream | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: /Mobi|Android|iPhone/i.test(navigator.userAgent)
            ? 'environment' // Use back camera on mobile
            : 'user', // Use webcam on desktop
        },
      })
      setStream(mediaStream)
      setCurrentState('camera')
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
      }
    } catch (err) {
      console.error('Error accessing camera:', err)
      showError('Unable to access camera. Please check permissions.')
    }
  }

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
      setCurrentState('upload')
    }
  }
  const takePhoto = () => {
    if (webcamRef.current) {
      const video = webcamRef.current.video
      if (!video) return

      // Create a canvas with proper aspect ratio
      const canvas = document.createElement('canvas')
      const videoWidth = video.videoWidth
      const videoHeight = video.videoHeight

      // Calculate silhouette dimensions - exactly matching detection
      const silhouette = {
        x: Math.floor(videoWidth * 0.45),
        y: Math.floor(videoHeight * 0.15),
        width: Math.floor(videoWidth * 0.1),
        height: Math.floor(videoHeight * 0.7),
      }

      // Set canvas size to match vertical orientation
      canvas.width = silhouette.width
      canvas.height = silhouette.height

      // Get the context and draw the cropped region
      const ctx = canvas.getContext('2d')
      if (ctx) {
        // Clear the canvas with white background
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // Draw the image maintaining vertical orientation
        ctx.drawImage(
          video,
          silhouette.x,
          silhouette.y,
          silhouette.width,
          silhouette.height,
          0,
          0,
          canvas.width,
          canvas.height,
        )

        // Convert to data URL with high quality
        const imageSrc = canvas.toDataURL('image/jpeg', 0.8)
        if (imageSrc) {
          const { file, error } = dataURLtoFile(imageSrc, 'camera-photo.jpg')
          if (file) {
            handleImageDrop([file])
            stopCamera()
          } else {
            showError(error as string)
          }
        }
      }
    }
  }



  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '500px',
        height: 'calc(100vh - 300px)',
        position: 'relative',
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 2,
        overflow: 'hidden',
        mx: 'auto',
      }}
    >
      <Webcam
        ref={webcamRef}
        audio={false}
        screenshotFormat="image/jpeg"
        videoConstraints={{
          width: 720,
          height: 1280,
          facingMode: /Mobi|Android|iPhone/i.test(navigator.userAgent)
            ? 'environment'
            : 'user',
        }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      <TestStripOverlay
        isCorrectlyPositioned={isStripPositioned}
        guidanceMessage={guidanceMessage}
      />
      <Stack
        direction={{ xs: 'row' }}
        spacing={2}
        justifyContent="center"
        sx={{
          position: 'absolute',
          bottom: '4%',
          left: 0,
          right: 0,
          px: 3,
          pt: 3,
          pb: 3,
          zIndex: 20,
          backgroundColor: 'transparent',
          '& button': {
            boxShadow: 2,
          },
        }}
      >
        <OutlinedButton
          fullWidth
          size="large"
          onClick={() => {
            stopCamera()
            setCurrentState('options')
          }}
          sx={{
            py: 1.5,
            px: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #D0D5DD',
            color: 'primary.dark',
            maxWidth: 200,
          }}
        >
          Cancel
        </OutlinedButton>
        <ContainedButton
          fullWidth
          size="large"
          onClick={takePhoto}
          disabled={!isStripPositioned}
          sx={{
            py: 1.5,
            px: 4,
            maxWidth: 200,
            opacity: isStripPositioned ? 1 : 0.6,
            '&.Mui-disabled': {
              backgroundColor: 'rgba(25, 118, 210, 0.5)',
              color: 'white',
            },
          }}
        >
          Take Photo
        </ContainedButton>
      </Stack>
    </Box>
  )

}